// Rich, grid-sized card for the integrated Train set picker (replaces the old
// overlay modal). Most fields come free from the list summary + the local
// progress store; the topic preview is passed in by the parent (fetched in the
// background, since topic labels live only on the full set).
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { QuestionSetSummary } from "@/api/library-types";
import { PRESENTATION, FALLBACK_PRESENTATION } from "@/create/framework-config";
import { progressStore } from "@/practice/progress-store";
import { cx } from "@/components/ui";

const FRAMEWORK_LABELS: Record<string, string> = {
  "learn-a-topic": "Learn a topic",
  "deep-dive": "Deep dive",
  "math-deep-dive": "Math deep dive",
  "de-trainer": "Deutsch-Trainer",
  "lecture-exercise-recap": "Lecture recap",
  "master-a-trait": "Master a trait",
  "combine-question-sets": "Combined",
};

export function frameworkLabel(slug: string | null, t: TFunction): string {
  if (!slug) return t("Custom");
  return FRAMEWORK_LABELS[slug] ? t(FRAMEWORK_LABELS[slug]) : t("Custom");
}

export function frameworkPresentation(slug: string | null) {
  return (slug && PRESENTATION[slug]) || FALLBACK_PRESENTATION;
}

export function formatSetDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}

/** "today" / "yesterday" / "Nd ago" within a week, else absolute date.
 *  Mirrors the iOS Library meta line's relative-then-absolute style. */
export function formatRelativeDate(iso: string | null | undefined, t: TFunction): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return t("today");
  if (days === 1) return t("yesterday");
  if (days < 7) return t("{{days}}d ago", { days });
  return d.toLocaleDateString();
}

/** Emoji-in-a-tile badge for the framework that generated the set. */
export function FrameworkBadge({ slug, className }: { slug: string | null; className?: string }) {
  const { t } = useTranslation();
  const p = frameworkPresentation(slug);
  return (
    <span
      className={cx(
        "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xl",
        p.tile,
        className,
      )}
      title={frameworkLabel(slug, t)}
    >
      {p.emoji}
    </span>
  );
}

export function setMetaLine(s: QuestionSetSummary, t: TFunction): string {
  const parts = [
    s.item_count === 1
      ? t("{{count}} item", { count: s.item_count })
      : t("{{count}} items", { count: s.item_count }),
    s.bulk_count === 1
      ? t("{{count}} topic", { count: s.bulk_count })
      : t("{{count}} topics", { count: s.bulk_count }),
  ];
  const date = s.created_at ? formatSetDate(s.created_at) : "";
  if (date) parts.push(date);
  return parts.join(" · ");
}

/** A rich set card for the on-page picker grid. */
export function SetCard({
  summary,
  active,
  topics,
  onSelect,
}: {
  summary: QuestionSetSummary;
  active: boolean;
  topics?: string[];
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const started = progressStore.hasStarted(summary.id);
  const attempts = progressStore.attemptCount(summary.id);
  const correct = progressStore.correctCount(summary.id);
  const accuracy = attempts > 0 ? Math.round((correct / attempts) * 100) : null;
  const doneBulks = progressStore.completedBulkCount(summary.id);
  const wrong = progressStore.wrongIds(summary.id).size;
  const lastAt = progressStore.lastActivityAt(summary.id);
  const pct =
    summary.bulk_count > 0 ? Math.min(100, Math.round((doneBulks / summary.bulk_count) * 100)) : 0;

  const topicPreview = topics && topics.length ? topics.slice(0, 3).join(" · ") : null;
  const moreTopics = topics && topics.length > 3 ? ` +${topics.length - 3}` : "";

  return (
    <button
      onClick={onSelect}
      title={active ? t("Open “{{title}}” in Library", { title: summary.title }) : undefined}
      className={cx(
        "card flex h-full flex-col gap-2 p-4 text-left transition",
        active
          ? "ring-2 ring-brand-400"
          : "hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-glow",
      )}
    >
      <div className="flex items-start gap-3">
        <FrameworkBadge slug={summary.source_framework_slug} />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-ink">{summary.title}</h3>
          <p className="mt-0.5 text-xs text-ink-faint">{setMetaLine(summary, t)}</p>
        </div>
      </div>

      {summary.description && (
        <p className="line-clamp-2 text-xs text-ink-soft">{summary.description}</p>
      )}

      {summary.project_id && (
        <span className="inline-flex w-fit items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700">
          📂 {summary.project_title
            ? t("From project: {{title}}", { title: summary.project_title })
            : t("From a project")}
        </span>
      )}

      {topicPreview && (
        <p className="truncate text-xs text-ink-faint">
          <span className="font-medium text-ink-soft">{t("Topics:")}</span> {topicPreview}
          {moreTopics}
        </p>
      )}

      {/* progress + accuracy (only once practised) */}
      {started && summary.bulk_count > 0 && (
        <div className="mt-auto pt-1">
          <div className="mb-1 flex items-center justify-between text-[11px] text-ink-faint">
            <span>
              {t("{{done}}/{{total}} sections", { done: doneBulks, total: summary.bulk_count })}
            </span>
            {accuracy !== null && <span>{t("{{pct}}% correct", { pct: accuracy })}</span>}
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-black/5">
            <div className="h-full rounded-full bg-brand-500" style={{ width: `${pct}%` }} />
          </div>
          {(wrong > 0 || lastAt) && (
            <div className="mt-1 flex items-center justify-between text-[10px] text-ink-faint">
              <span className={wrong > 0 ? "font-medium text-red-600" : ""}>
                {wrong > 0 ? t("{{count}} to review", { count: wrong }) : ""}
              </span>
              {lastAt && <span>{formatRelativeDate(lastAt, t)}</span>}
            </div>
          )}
        </div>
      )}

      <span
        className={cx(
          "mt-1 inline-block w-fit rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
          active
            ? "bg-brand-600 text-white"
            : started
              ? "bg-amber-100 text-amber-700"
              : "bg-black/5 text-ink-faint",
        )}
      >
        {active ? t("Training now") : started ? t("In progress") : t("New")}
      </span>
      {active && (
        <span className="text-[10px] font-medium text-brand-600">
          {t("Tap again to open in Library →")}
        </span>
      )}
    </button>
  );
}
